import { useEffect, useState, FC, useMemo } from "react";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import "./dev.scss";
import { RubiOrgModel, getOrg, patchOrgAdmin } from "@rubi/api-generated";
import { Link, useLocation, useParams } from "react-router-dom";

import { Breadcrumbs, Crumb, DevPage, ErrorMsg, Section } from "./common";

// Canonical app namespaces an org can be granted. Mirrors the `app.*` values
// in components/UserManagement/types.ts — kept inline because the dev tool
// shouldn't pull the bigger Permission enum just for this picker.
const KNOWN_APPS = [
  "app.alert.",
  "app.report.",
  "app.report_v2.",
  "app.camera_registry.",
  "app.registry.",
  "app.message.",
  "app.intel.",
  "app.connect.",
  "app.partner.",
];

const toStringArray = (v: unknown): string[] => {
  if (Array.isArray(v))
    return v.filter((x): x is string => typeof x === "string");
  return [];
};

const formatJson = (v: unknown): string => {
  if (v == null) return "";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return "";
  }
};

const stableJson = (v: unknown): string => {
  try {
    return JSON.stringify(v ?? null);
  } catch {
    return "";
  }
};

type MapCenter = { lat: number; lng: number };

const parseMapCenter = (v: unknown): MapCenter | null => {
  if (
    v != null &&
    typeof v === "object" &&
    typeof (v as { lat?: unknown }).lat === "number" &&
    typeof (v as { lng?: unknown }).lng === "number"
  ) {
    const o = v as MapCenter;
    return { lat: o.lat, lng: o.lng };
  }
  return null;
};

export const DevOrgPage: FC = () => {
  const location = useLocation();
  const { id } = useParams();
  const [org, setOrg] = useState<RubiOrgModel | null>(null);
  const [loadOrgErrorMsg, setLoadOrgErrorMsg] = useState<string | null>(null);

  // Form state — initialised from the loaded org and re-synced when reset/save.
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [approvedApps, setApprovedApps] = useState<string[]>([]);
  const [defaultPermsJson, setDefaultPermsJson] = useState("");
  const [defaultPermsError, setDefaultPermsError] = useState<string | null>(
    null,
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const seedForm = (o: RubiOrgModel) => {
    setTitle(o.title ?? "");
    setShortTitle(o.short_title ?? "");
    setLogoUrl(o.logo_url ?? "");
    const mc = parseMapCenter(o.map_center);
    setLat(mc ? String(mc.lat) : "");
    setLng(mc ? String(mc.lng) : "");
    setApprovedApps(toStringArray(o.approved_apps));
    setDefaultPermsJson(formatJson(o.default_app_permissions));
    setDefaultPermsError(null);
  };

  const loadOrg = async (orgId: string) => {
    const response = await getOrg({
      path: { org_id: orgId },
      throwOnError: false,
    });
    if (response.status === 200) {
      setOrg(response.data!);
      seedForm(response.data!);
    } else {
      setLoadOrgErrorMsg(
        "" + response.status + " " + response.error?.console_msg,
      );
    }
  };

  useEffect(() => {
    if (location.state?.org && location.state.org.id == id) {
      setOrg(location.state.org);
      seedForm(location.state.org);
    } else {
      if (id) {
        loadOrg(id);
      }
    }
  }, [id]);

  // Validate the JSON editor on every change so we can show inline error and
  // gate save.
  const parsedDefaultPerms = useMemo(() => {
    const trimmed = defaultPermsJson.trim();
    if (trimmed === "") return { ok: true as const, value: null };
    try {
      return { ok: true as const, value: JSON.parse(trimmed) as unknown };
    } catch (e) {
      return {
        ok: false as const,
        error: e instanceof Error ? e.message : "Invalid JSON",
      };
    }
  }, [defaultPermsJson]);

  useEffect(() => {
    setDefaultPermsError(
      parsedDefaultPerms.ok ? null : parsedDefaultPerms.error,
    );
  }, [parsedDefaultPerms]);

  const latNum = lat.trim() === "" ? null : Number.parseFloat(lat);
  const lngNum = lng.trim() === "" ? null : Number.parseFloat(lng);
  const mapCenterInvalid =
    (latNum !== null && Number.isNaN(latNum)) ||
    (lngNum !== null && Number.isNaN(lngNum)) ||
    (latNum === null) !== (lngNum === null);

  // Build the dirty diff against the loaded org so we only PATCH what changed.
  const dirtyBody = useMemo(() => {
    if (!org) return null;
    const body: Record<string, unknown> = {};

    if (title !== (org.title ?? "")) body.title = title;
    if (shortTitle !== (org.short_title ?? "")) {
      body.short_title = shortTitle === "" ? null : shortTitle;
    }
    if (logoUrl !== (org.logo_url ?? "")) {
      body.logo_url = logoUrl === "" ? null : logoUrl;
    }

    const currentApps = toStringArray(org.approved_apps);
    if (
      stableJson([...approvedApps].sort()) !==
      stableJson([...currentApps].sort())
    ) {
      body.approved_apps = approvedApps;
    }

    if (parsedDefaultPerms.ok) {
      if (
        stableJson(parsedDefaultPerms.value) !==
        stableJson(org.default_app_permissions ?? null)
      ) {
        body.default_app_permissions = parsedDefaultPerms.value;
      }
    }

    const currentMc = parseMapCenter(org.map_center);
    const nextMc =
      latNum !== null && lngNum !== null && !mapCenterInvalid
        ? { lat: latNum, lng: lngNum }
        : null;
    if (stableJson(currentMc) !== stableJson(nextMc)) {
      body.map_center = nextMc;
    }

    return body;
  }, [
    org,
    title,
    shortTitle,
    logoUrl,
    approvedApps,
    parsedDefaultPerms,
    latNum,
    lngNum,
    mapCenterInvalid,
  ]);

  const isDirty = dirtyBody != null && Object.keys(dirtyBody).length > 0;
  const canSave = isDirty && !saving && !defaultPermsError && !mapCenterInvalid;

  const handleSave = async () => {
    if (!org || !id || !dirtyBody) return;
    setSaving(true);
    setSaveError(null);
    try {
      const response = await patchOrgAdmin({
        path: { org_id: id },
        body: dirtyBody,
      });
      const updated = response.data!;
      setOrg(updated);
      seedForm(updated);
      setSaveSuccess(true);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (org) seedForm(org);
  };

  return (
    <DevPage
      title={
        (org?.title || "Unknown") +
        ((org?.short_title && " (" + org!.short_title + ")") || "")
      }
      breadcrumbs={
        <Breadcrumbs>
          <Crumb>
            <Link to="/">Home</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin">Developer and Administrator Utilities</Link>
          </Crumb>
          <Crumb>
            <Link to="/dev_admin/org">Orgs</Link>
          </Crumb>
          <Crumb>{id}</Crumb>
        </Breadcrumbs>
      }
    >
      <Section subtitle="Details">
        <ErrorMsg msg={loadOrgErrorMsg} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>id</td>
              <td className="mono">{org?.id}</td>
            </tr>
            <tr>
              <td>stytch_org_id</td>
              <td className="mono">
                {org?.stytch_org_id || <span className="muted">—</span>}
              </td>
            </tr>
            <tr>
              <td>api_region</td>
              <td>{org?.api_region}</td>
            </tr>
            <tr>
              <td>time_created</td>
              <td className="mono muted">{org?.time_created}</td>
            </tr>
            <tr>
              <td>time_updated</td>
              <td className="mono muted">{org?.time_updated}</td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section subtitle="Update">
        {!org ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Edit the <code>rubi_org</code> row directly. Empty values clear
              the column (sent as null). Use the Org Config page for
              configuration fields like email templates and incident numbering.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={title.trim() === ""}
                  helperText={
                    title.trim() === "" ? "Title cannot be empty" : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Short title"
                  value={shortTitle}
                  onChange={(e) => setShortTitle(e.target.value)}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  helperText="Path or URL to the org logo (e.g. /service-logos/foo.png). Separate from the org_config email logo."
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Map centre latitude"
                  value={lat}
                  onChange={(e) => setLat(e.target.value)}
                  error={mapCenterInvalid}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Map centre longitude"
                  value={lng}
                  onChange={(e) => setLng(e.target.value)}
                  error={mapCenterInvalid}
                  helperText={
                    mapCenterInvalid
                      ? "Provide both latitude and longitude as numbers, or leave both blank."
                      : "Used to centre the public alert map. Leave both blank to clear."
                  }
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={KNOWN_APPS}
                  value={approvedApps}
                  onChange={(_e, v) => setApprovedApps(v)}
                  renderTags={(value: readonly string[], getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Approved apps"
                      helperText="Apps this org is licenced for. Pick from the known list or type a custom namespace (e.g. app.foo.)."
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={6}
                  label="Default app permissions (JSON)"
                  value={defaultPermsJson}
                  onChange={(e) => setDefaultPermsJson(e.target.value)}
                  error={!!defaultPermsError}
                  helperText={
                    defaultPermsError ??
                    "JSON object applied as the starting permission set for new users in this org."
                  }
                  inputProps={{
                    style: { fontFamily: "monospace", fontSize: 13 },
                  }}
                />
              </Grid>
            </Grid>

            {saveError && <Alert severity="error">{saveError}</Alert>}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={handleReset} disabled={!isDirty || saving}>
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Section>

      <Section subtitle="Delete">
        <Box sx={{ color: "text.secondary" }}>
          Deletion is not exposed here yet — use a database session if you need
          to remove an org.
        </Box>
      </Section>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          Saved
        </Alert>
      </Snackbar>
    </DevPage>
  );
};
